import classNames from 'classnames';
import { isSameDay } from 'date-fns/isSameDay';
import * as React from 'react';
import type { ReactNode } from 'react';
import { Button } from '~/Partials/Button/Button';
import { ButtonSize } from '~/Partials/Button/ButtonSize.csharp';
import { ButtonTier } from '~/Partials/Button/ButtonTier.csharp';
import { ButtonType } from '~/Partials/Button/ButtonType.csharp';
import { SvgIcon } from '~/Partials/SvgIcon/SvgIcon';
import { useChangeObserver } from '~/Utils/Hooks/useChangeObserver';
import styles from './TimeFrameSwitcher.module.css';

export interface TimeFrameSwitcherTranslations {
    nextTimeFrame: string;
    previousTimeFrame: string;
}

export interface TimeFrameSwitcherProps<TCustomHeaderProps> {
    customHeaderProps: TCustomHeaderProps;
    dateRenderer: (date: Date) => ReactNode;
    onPreviousTimeFrameClick: (props: TCustomHeaderProps) => void;
    onNextTimeFrameClick: (props: TCustomHeaderProps) => void;
    translations: TimeFrameSwitcherTranslations;
    onSelectionChanged?: (props: TCustomHeaderProps) => void;
    withWeekNumbers?: boolean;
    className?: string;
}

export const TimeFrameSwitcher = <TCustomHeaderProps extends { date: Date }>(
    props: TimeFrameSwitcherProps<TCustomHeaderProps>
) => {
    const {
        customHeaderProps,
        onPreviousTimeFrameClick,
        onNextTimeFrameClick,
        translations,
        dateRenderer,
        className,
        onSelectionChanged,
        withWeekNumbers = true,
    } = props;

    const { date } = customHeaderProps;

    useChangeObserver(date, () => onSelectionChanged?.(customHeaderProps), isSameDay);

    const buttonBaseProps = {
        size: ButtonSize.XSmall,
        tier: ButtonTier.Tertiary,
        type: ButtonType.Solid,
        className: styles.Button,
    };

    return (
        <div
            className={classNames(
                styles.TimeFrameSwitcher,
                withWeekNumbers && styles['Variant--withWeekNumbers'],
                className
            )}
        >
            <Button
                {...buttonBaseProps}
                onClick={() => onPreviousTimeFrameClick(customHeaderProps)}
                title={translations.previousTimeFrame}
            >
                <SvgIcon iconName="arrow-left" className={styles.ButtonIcon} />
                <span className="visuallyHidden">{translations.previousTimeFrame}</span>
            </Button>
            <div>
                <span className={styles.CurrentDateLabel}>{dateRenderer(date)}</span>
            </div>
            <Button
                {...buttonBaseProps}
                onClick={() => onNextTimeFrameClick(customHeaderProps)}
                title={translations.nextTimeFrame}
            >
                <SvgIcon iconName="arrow-right" className={styles.ButtonIcon} />
                <span className="visuallyHidden">{translations.nextTimeFrame}</span>
            </Button>
        </div>
    );
};
